import { FC } from "react";
import { Paper, Box, Typography, Divider, Grid, Chip } from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  BarChart as BarChartIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { ExportDataItemDto } from "../../../../../../../api-client";

interface ExportDetailsProps {
  exportItem: ExportDataItemDto;
}

export const ExportDetails: FC<ExportDetailsProps> = ({ exportItem }) => {
  const { formattedPayload, metadata } = exportItem;

  const formatCurrency = (value: number) =>
    `${value.toFixed(2).replace(".", ",")} €`;

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#D9DFE2",
        border: "1px solid #e0e0e0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          bgcolor: "white",
          color: "black",
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <ReceiptIcon fontSize="small" sx={{ color: "#757575" }} />
        <Typography variant="body2" fontWeight="500">
          Export Informationen
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f5f5f5",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#bdbdbd",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#9e9e9e",
          },
        }}
      >
        {/* Metadata Section */}
        {metadata && (
          <Box
            sx={{
              p: 1,
              mb: 1,
              borderRadius: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
            >
              <InfoIcon sx={{ fontSize: 14, color: "#757575" }} />
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{ color: "#757575" }}
              >
                Datei-Metadaten
              </Typography>
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Sender IK
                </Typography>
                <Typography variant="body2" fontWeight="500" color="black">
                  {metadata.senderIk}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Empfänger IK
                </Typography>
                <Typography variant="body2" fontWeight="500" color="black">
                  {metadata.receiverIk}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Phys. Sender
                </Typography>
                <Typography variant="body2" fontWeight="500" color="black">
                  {metadata.physicalSenderIk}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Phys. Empfänger
                </Typography>
                <Typography variant="body2" fontWeight="500" color="black">
                  {metadata.physicalReceiverIk}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.3,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    fontWeight="500"
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Übertragen am
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    fontWeight="500"
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Zeit
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontWeight="500" color="black">
                    {new Date(metadata.transmissionDate).toLocaleDateString(
                      "de-DE",
                      {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      },
                    )}
                  </Typography>
                  <Typography variant="body2" fontWeight="500" color="#9C1A35">
                    {new Date(metadata.transmissionDate).toLocaleTimeString(
                      "de-DE",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      },
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Transfer-Nr.
                </Typography>
                <Typography variant="body2" fontWeight="500" color="black">
                  {metadata.transferNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Verschlüsselt
                </Typography>
                <Typography variant="body2" fontWeight="500" color="#9C1A35">
                  {formatBytes(metadata.encryptedSize)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Logischer Dateiname
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="black"
                  sx={{ wordBreak: "break-all" }}
                >
                  {metadata.logicalFileName}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight="500"
                  sx={{
                    mb: 0.3,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Physischer Dateiname
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="black"
                  sx={{ wordBreak: "break-all" }}
                >
                  {metadata.physicalFileName}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Payload Section */}
        {formattedPayload && (
          <>
            {/* Rx Numbers */}
            {formattedPayload.rxNumbers &&
              formattedPayload.rxNumbers.length > 0 && (
                <Box
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 3,
                    bgcolor: "white",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 14, color: "#757575" }} />
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      sx={{ color: "#757575" }}
                    >
                      Rezepte ({formattedPayload.rxNumbers.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {formattedPayload.rxNumbers.map((rxNumber, index) => (
                      <Chip
                        key={index}
                        label={rxNumber}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.8rem",
                          bgcolor: "white",
                          color: "#9C1A35",
                          fontWeight: 500,
                          border: "1px solid #e0e0e0",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

            {/* Total Amounts */}
            {formattedPayload.totalAmounts && (
              <Box
                sx={{
                  mb: 1,
                  p: 1,
                  borderRadius: 3,
                  bgcolor: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <EuroIcon sx={{ fontSize: 14, color: "#757575" }} />
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    sx={{ color: "#757575" }}
                  >
                    Gesamtbeträge
                  </Typography>
                </Box>
                <Grid container spacing={0.75}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 0.75,
                        bgcolor: "#ffffff",
                        borderRadius: 3,
                        mb: 0.1,
                      }}
                    >
                      <Typography variant="body2" fontWeight="400">
                        Bruttobetrag
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        color="#757575"
                      >
                        {formatCurrency(
                          formattedPayload.totalAmounts.grossValue,
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 0.75,
                        bgcolor: "#ffffff",
                        borderRadius: 3,
                        mb: 0.1,
                      }}
                    >
                      <Typography variant="body2" fontWeight="400">
                        Rechnungsbetrag
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        color="#757575"
                      >
                        {formatCurrency(
                          formattedPayload.totalAmounts.billValue,
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 0.75,
                        bgcolor: "#ffffff",
                        borderRadius: 3,
                      }}
                    >
                      <Typography variant="body2" fontWeight="400">
                        Zuzahlung
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        color="#757575"
                      >
                        {formatCurrency(
                          formattedPayload.totalAmounts.copayment,
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Status Amounts */}
            {formattedPayload.statusAmounts && (
              <>
                {formattedPayload.statusAmounts.status10000 && (
                  <Box
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 3,
                      bgcolor: "white",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <BarChartIcon sx={{ fontSize: 14, color: "#757575" }} />
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{ color: "#757575" }}
                      >
                        Status 10000 (Genehmigt)
                      </Typography>
                    </Box>
                    <Grid container spacing={0.75}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Rechnung
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status10000
                                .billValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Brutto
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status10000
                                .grossValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {formattedPayload.statusAmounts.status30000 && (
                  <Box
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 3,
                      bgcolor: "white",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <BarChartIcon sx={{ fontSize: 14, color: "#757575" }} />
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{ color: "#757575" }}
                      >
                        Status 30000 (Teilweise abgelehnt)
                      </Typography>
                    </Box>
                    <Grid container spacing={0.75}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Rechnung
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status30000
                                .billValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Brutto
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status30000
                                .grossValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {formattedPayload.statusAmounts.status50000 && (
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 3,
                      bgcolor: "white",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <BarChartIcon sx={{ fontSize: 14, color: "#757575" }} />
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{ color: "#757575" }}
                      >
                        Status 50000 (Abgelehnt)
                      </Typography>
                    </Box>
                    <Grid container spacing={0.75}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Rechnung
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status50000
                                .billValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 0.2,
                            bgcolor: "#ffffff",
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Brutto
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="500"
                            color="#757575"
                          >
                            {formatCurrency(
                              formattedPayload.statusAmounts.status50000
                                .grossValue,
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
